import React, { useState, useEffect } from 'react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';

const ContestPhaseIndicator = ({ startDate, submissionsEndDate, endDate }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [currentPhase, setCurrentPhase] = useState('');
  const [previousTimeLeft, setPreviousTimeLeft] = useState('');
  // progress will be a number from 0 to 100,
  // where 0 represents the start of the contest,
  // 50 is submission end, and 100 is contest end.
  const [progress, setProgress] = useState(0);

  const phases = [
    { id: 'submission', label: 'Submission' },
    { id: 'voting', label: 'Voting' },
    { id: 'cooldown', label: 'End' }
  ];

  const getPhaseIndex = (phaseId) => {
    return phases.findIndex(p => p.id === phaseId);
  };

  const formatTimeLeft = (difference) => {
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    const parts = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    if (seconds > 0) parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);

    return parts.join(' ');
  };

  const formatShortDate = (timestamp) => {
  const date = new Date(timestamp);
  // This will produce a format like "Jul 5, 7:00 AM"
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

  // Compute phase dates from props.
  const start =
    typeof startDate === 'number'
      ? parseInt(startDate) * 1000
      : new Date(startDate).getTime();
  const submissionEnd =
    typeof submissionsEndDate === 'number'
      ? parseInt(submissionsEndDate) * 1000
      : new Date(submissionsEndDate).getTime();
  const contestEnd =
    typeof endDate === 'number'
      ? parseInt(endDate) * 1000
      : new Date(endDate).getTime();

  const formatDate = (timestamp) => new Date(timestamp).toLocaleString();

  const determinePhaseAndTime = () => {
    const now = new Date().getTime();

    let targetDate;
    let phaseText;
    let newPhase;
    let newProgress;

    if (now < submissionEnd) {
      // Submission phase
      targetDate = submissionEnd;
      phaseText = 'Time left to submit:';
      newPhase = 'submission';

      // Calculate progress for submission phase (0 to 50%)
      const submissionPhaseTotal = submissionEnd - start;
      const submissionPhaseElapsed = now - start;
      newProgress = (submissionPhaseElapsed / submissionPhaseTotal) * 50;
    } else if (now < contestEnd) {
      // Voting phase
      targetDate = contestEnd;
      phaseText = 'Time left to vote:';
      newPhase = 'voting';

      // Calculate progress for voting phase (50 to 100%)
      const votingPhaseTotal = contestEnd - submissionEnd;
      const votingPhaseElapsed = now - submissionEnd;
      newProgress = 50 + (votingPhaseElapsed / votingPhaseTotal) * 50;
    } else {
      // Contest is over – mark as cooldown/completed.
      setPreviousTimeLeft(timeLeft);
      setTimeLeft('Contest has ended');
      setCurrentPhase('cooldown');
      setProgress(100);
      return;
    }

    const difference = targetDate - now;

    if (difference > 0) {
      const formattedTime = formatTimeLeft(difference);
      const newTimeLeft = `${phaseText} ${formattedTime}`;
      setPreviousTimeLeft(timeLeft);
      setTimeLeft(newTimeLeft);
      setCurrentPhase(newPhase);
      setProgress(newProgress);
    }
  };

  useEffect(() => {
    determinePhaseAndTime(); // Initial call
    const timer = setInterval(determinePhaseAndTime, 1000);
    return () => clearInterval(timer);
  }, [submissionsEndDate, endDate]);

  const displayedTimeLeft = timeLeft || previousTimeLeft;

  /* 
    Calculate progress track widths.
  */
  let passedWidth = 0;
  let currentWidth = 0;
  if (currentPhase === 'submission') {
    passedWidth = 0;
    currentWidth = progress; // 0 - 50%
  } else if (currentPhase === 'voting') {
    passedWidth = 50;
    currentWidth = progress - 50; // 0 - 50%
  } else if (currentPhase === 'cooldown') {
    passedWidth = 100;
    currentWidth = 0;
  }

  return (
    <div className="mb-4">
      <div className="position-relative mt-5 mb-4" style={{  }}>
        {/* Progress track container */}
        <div
          className="progress-track"
          style={{
            position: 'absolute',
            left: '16.666%',
            right: '16.666%',
            height: '2px',
            backgroundColor: '#dee2e6',
            // top: '50%',
            top: '2rem',
            transform: 'translateY(-0.75rem)'
          }}
        >
          {/* Faded background for completed phases */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              height: '100%',
              width: currentPhase === 'cooldown' ? '100%' : `${passedWidth}%`,
              backgroundColor: 'rgba(13, 110, 253, 0.2)',
              transition: 'width 0.5s ease-in-out'
            }}
          />

          {/* Current phase progress */}
          {currentWidth > 0 && currentPhase !== 'cooldown' && (
            <div
              style={{
                position: 'absolute',
                left: `${passedWidth}%`,
                height: '100%',
                width: `${currentWidth}%`,
                backgroundColor: 'rgba(13, 110, 253, 0.5)',
                transition: 'width 0.5s ease-in-out'
              }}
            />
          )}
        </div>

        {/* Circles with tooltips */}
        <Row className="position-relative" style={{ marginTop: '-1rem' }}>
          {phases.map((p, index) => {
            // Build tooltip content based on phase
            let tooltipContent;
            if (p.id === 'submission') {
              tooltipContent = (
                <div>
                  <div>
                    <strong>Submission Phase</strong>
                  </div>
                  <div>Starts: {formatDate(start)}</div>
                  <div>Ends: {formatDate(submissionEnd)}</div>
                  <div>Duration: {formatTimeLeft(submissionEnd - start)}</div>
                </div>
              );
            } else if (p.id === 'voting') {
              tooltipContent = (
                <div>
                  <div>
                    <strong>Voting Phase</strong>
                  </div>
                  <div>Starts: {formatDate(submissionEnd)}</div>
                  <div>Ends: {formatDate(contestEnd)}</div>
                  <div>Duration: {formatTimeLeft(contestEnd - submissionEnd)}</div>
                </div>
              );
            } else if (p.id === 'cooldown') {
              tooltipContent = (
                <div>
                  <div>
                    <strong>End</strong>
                  </div>
                  <div>Ends: {formatDate(contestEnd)}</div>
                </div>
              );
            }

            const isCurrentPhase = currentPhase === p.id;
            const isPastPhase = getPhaseIndex(currentPhase) > getPhaseIndex(p.id);

            return (
              <Col key={p.id} className="text-center">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id={`tooltip-${p.id}`}>{tooltipContent}</Tooltip>}
                >
                  <div
                    className={`rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-2
                      ${isCurrentPhase ? 'bg-primary text-white' :
                        isPastPhase ? 'bg-primary text-muted opacity-50' :
                        'bg-light text-muted'}`}
                    style={{
                      width: '2.5rem',
                      height: '2.5rem',
                      backgroundImage: 'url(https://www.hearthcards.net/images/contest/ring.webp)',
                      backgroundSize: 'cover',
                      border: isCurrentPhase ? 'none' : '1px solid #dee2e6',
                      transition: 'all 0.3s ease-in-out',
                      position: 'relative',
                      zIndex: 1,
                      cursor: 'pointer'
                    }}
                  >
                    {index + 1}
                  </div>
                </OverlayTrigger>

    <div className="small text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
      {p.id === 'submission' && formatShortDate(start)}
      {p.id === 'voting' && formatShortDate(submissionEnd)}
      {p.id === 'cooldown' && formatShortDate(contestEnd)}
    </div>

                <div
                  className={`small fw-bold
                    ${isCurrentPhase ? 'text-primary' :
                      isPastPhase ? 'text-muted opacity-50' :
                      'text-muted'}`}
                >
                  {p.label}
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
    </div>
  );
};

export default ContestPhaseIndicator;
